/**
 * The frame of every signed-in screen: a navigation sidebar, an account menu and the page. The
 * caller passes the navigation its person may use, so the shell shows no link to a screen they
 * cannot open.
 *
 * From 1280px the sidebar is full. From 1024 to 1279 it is an icon rail whose items name
 * themselves in a tooltip. Below 1024 there is a top bar with the brand and a Menu button, and the
 * navigation opens in a drawer; the page takes the full width. The current item carries
 * `aria-current="page"`. A "Skip to content" link is the first stop on the page.
 */
import * as stylex from "@stylexjs/stylex";
import type { ReactNode } from "react";
import { useEffect, useId, useRef, useState } from "react";
import "@lattice/tokens/breakpoints.stylex";
import { breakpoints } from "@lattice/tokens/breakpoints.stylex";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/layer.stylex";
import { layer } from "@lattice/tokens/layer.stylex";
import "@lattice/tokens/size.stylex";
import { size } from "@lattice/tokens/size.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import "@lattice/tokens/space.stylex";
import { border, radius, space } from "@lattice/tokens/space.stylex";
import { Avatar } from "./Avatar.tsx";
import { Button } from "./Button.tsx";
import { Dialog } from "./Dialog.tsx";
import { focusRing, focusRingInset } from "./focus.ts";
import type { Navigate } from "./Link.tsx";
import { followWith } from "./Link.tsx";
import type { MenuItem } from "./Menu.tsx";
import { Menu } from "./Menu.tsx";
import { Text } from "./Text.tsx";
import { textStyle } from "./textStyle.ts";
import { Tooltip } from "./Tooltip.tsx";
import { useMedia } from "./useMedia.ts";
import { visuallyHidden } from "./visuallyHidden.ts";
const styles = {
  skip: {
    kVAEAm: "x10l6tqk",
    k87sOh: "xhwcp2v",
    kLqNvP: "x1eua9zb",
    kY2c9j: "xq3i3c4",
    k8WAf4: "x1jyo4x1",
    kg3NbH: "x3i8z2n",
    kaIpWk: "x1fbwzx",
    kWkggS: "x40k5rp",
    kMwMTN: "xm8xz4y",
    k3aq6I: "xr996gy x1cq5w7s",
    $$css: true
  },
  link: {
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kOIVth: "x1173whf",
    kAzted: "xn2nem0 xlivvtk xei8rr2",
    kB7OPa: "x9f619",
    kg3NbH: "xc7sml1",
    kaIpWk: "x1fbwzx",
    kMwMTN: "xm8xz4y",
    kMnn75: "xkrqix3",
    kWkggS: "xjbqb8w x14uvmqf xhdl3qd",
    $$css: true
  },
  current: {
    kWkggS: "x1jct50n",
    k63SB2: "x1s688f",
    $$css: true
  },
  railLink: {
    kjj79g: "xl56j7k",
    kzqmXN: "x150l1ig",
    kg3NbH: "xnjsko4",
    $$css: true
  }
};
export interface NavItem {
  readonly label: string;
  readonly href: string;
  /** Drawn before the label, and alone in the icon rail where the label is hidden. Every item has one. */
  readonly icon: ReactNode;
  readonly current?: boolean;
}
export interface NavGroup {
  readonly label?: string | undefined;
  readonly items: readonly NavItem[];
}
export interface AppShellProps {
  /** The product's name, at the top of the sidebar. */
  readonly brand: string;
  readonly nav: readonly NavGroup[];
  /** What a screen reader calls the navigation, when "Main" would leave two apps alike. */
  readonly navLabel?: string;
  /** Who is signed in, with the menu of what they can do about it: account, sign out. */
  readonly account: {
    readonly name: string;
    readonly items: readonly MenuItem[];
  };
  readonly navigate?: Navigate;
  readonly testId?: string;
  readonly children: ReactNode;
}
function NavLink({
  item,
  rail,
  navigate,
  onFollow,
  initialFocus = false
}: {
  readonly item: NavItem;
  readonly rail: boolean;
  readonly navigate: Navigate | undefined;
  readonly onFollow?: (() => void) | undefined;
  /** First focus when the sheet opens, set on the current page's link so the person starts where they are. */
  readonly initialFocus?: boolean;
}) {
  const tipId = useId();
  const follow = followWith(navigate, item.href);
  const link = <a href={item.href} aria-current={item.current === true ? "page" : undefined} data-initial-focus={initialFocus && item.current === true ? "true" : undefined} onClick={event => {
    follow(event);
    onFollow?.();
  }} {...stylex.props(styles.link, textStyle.label, item.current === true && styles.current, rail && styles.railLink, focusRingInset)}>
      <span aria-hidden="true" className="x3nfvp2 x6s0dn4 xl56j7k x2lah0s x1z0q0p5 xq9ob8z">
        {item.icon}
      </span>
      <span {...stylex.props(rail && visuallyHidden.hidden)}>{item.label}</span>
    </a>;
  return <li>
      {rail ? <Tooltip id={tipId} text={item.label}>
          {link}
        </Tooltip> : link}
    </li>;
}
function NavList({
  label,
  groups,
  rail,
  navigate,
  onFollow,
  initialFocus = false
}: {
  readonly label: string;
  readonly groups: readonly NavGroup[];
  readonly rail: boolean;
  readonly navigate: Navigate | undefined;
  readonly onFollow?: () => void;
  readonly initialFocus?: boolean;
}) {
  return <nav aria-label={label} className="x78zum5 xdt5ytf x1r0x45p x1ghz6dp x1717udv">
      {groups.map((group, at) => <div key={group.label ?? at}>
          {group.label === undefined || rail ? null : <div className="xc7sml1 xnjqvx5">
              <Text variant="caption" tone="secondary">
                {group.label}
              </Text>
            </div>}
          <ul className="x78zum5 xdt5ytf x15xv0s5 x1ghz6dp x1717udv xe8uvvx">
            {group.items.map(item => <NavLink key={item.href} item={item} rail={rail} navigate={navigate} onFollow={onFollow} initialFocus={initialFocus} />)}
          </ul>
        </div>)}
    </nav>;
}
export function AppShell({
  brand,
  nav,
  navLabel = "Main",
  account,
  navigate,
  testId,
  children
}: AppShellProps) {
  const desktop = useMedia("(min-width: 1024px)");
  const rail = useMedia("(min-width: 1024px) and (max-width: 1279px)");
  const [open, setOpen] = useState(false);
  const top = useRef<HTMLDivElement>(null);

  /* The page scrolls the document, so the padding goes on it: a control focused below the fold
     scrolls into view under the sticky top bar otherwise. Measured, since the bar grows with text. */
  useEffect(() => {
    const bar = top.current;
    if (desktop || bar === null) return;
    const root = document.documentElement;
    const apply = () => {
      root.style.scrollPaddingBlockStart = `${bar.getBoundingClientRect().height}px`;
    };
    apply();
    const watch = new ResizeObserver(apply);
    watch.observe(bar);
    return () => {
      watch.disconnect();
      root.style.scrollPaddingBlockStart = "";
    };
  }, [desktop]);
  const accountTip = useId();
  /* The bar names the page the person is on, so a phone shows where it is without opening the menu. It is
     hidden from a screen reader because the page's heading says the same. */
  const here = nav.flatMap(group => group.items).find(item => item.current === true);
  return <div data-testid={testId} {...{
    0: {
      className: "x78zum5 x1ov3xa9 x1mfo6jk xm8xz4y x1cikjda xjmaktb xejwzen"
    },
    1: {
      className: "x78zum5 x1ov3xa9 x1mfo6jk xm8xz4y x1cikjda xjmaktb xejwzen xdt5ytf"
    }
  }[!!!desktop << 0]}>
      <a href="#content" {...stylex.props(styles.skip, textStyle.label, focusRing)}>
        Skip to content
      </a>
      {desktop ? <div {...{
      0: {
        className: "x7wzq59 x13vifvy x1y01iyo x78zum5 xdt5ytf x2lah0s x1r0x45p xqcrz7y x9f619 xtdtrs8 x1a91ssu x1odjw0f x14ba300 x8mat17 x18b5jzi x1cxcgbd xb07vcz"
      },
      1: {
        className: "x7wzq59 x13vifvy x1y01iyo x78zum5 xdt5ytf x2lah0s x1r0x45p xqcrz7y x9f619 xtdtrs8 x1a91ssu x1odjw0f x14ba300 x8mat17 x18b5jzi x1cxcgbd xwk4xl1 x6s0dn4 x1lq7ia9"
      }
    }[!!rail << 0]}>
          {rail ? null : <Text variant="label">
              <span className="xc7sml1 x1s688f xjat59b">{brand}</span>
            </Text>}
          <NavList label={navLabel} groups={nav} rail={rail} navigate={navigate} />
          <div className="xr1yuqi">
            {rail ? <Tooltip id={accountTip} text={account.name}>
                <Menu label={`Account menu for ${account.name}`} items={account.items} side="top" icon={<span className="x3nfvp2 xdq7b4a x1y0btm7 xv9mh3h x1m3fsnl">
                      <Avatar name={account.name} email={null} />
                    </span>} />
              </Tooltip> : <Menu label={`Account menu for ${account.name}`} items={account.items} side="top" triggerContent={account.name} nameTrigger block />}
          </div>
        </div> : <div ref={top} className="x7wzq59 x13vifvy x1y01iyo x78zum5 x6s0dn4 x1qughib x626il7 x9f619 x10n3o9p x3i8z2n x1tg053j x14ba300 xq3vdme x1q0q8m5 x10xjq5j">
          <div className="x78zum5 x1pha0wt xskm3c9 xeuugli">
            <Text variant="label">
              <span className="xnjsko4 x1s688f xjat59b">{brand}</span>
            </Text>
            {here === undefined ? null : <span aria-hidden="true" className="xb3r6kr xlyipyv xuxw1ft">
                <Text as="span" variant="caption" tone="secondary">
                  {here.label}
                </Text>
              </span>}
          </div>
          <Button corners="rounded" variant="secondary" size="md" onClick={() => setOpen(true)}>
            Menu
          </Button>
          <Dialog open={open} variant="sheet" title="Menu" closeLabel="Close menu" onClose={() => setOpen(false)}>
            <NavList label={navLabel} groups={nav} rail={false} navigate={navigate} onFollow={() => setOpen(false)} initialFocus />
            <div className="xr1yuqi xeonxcd x153ygsw x13fuv20 x8me8j4">
              <Menu label={`Account menu for ${account.name}`} triggerContent={account.name} nameTrigger block items={account.items.map(item => ({
            ...item,
            // The drawer would otherwise stay open over whatever the item shows, such as a failure notice.
            onSelect: () => {
              setOpen(false);
              item.onSelect();
            }
          }))} />
            </div>
          </Dialog>
        </div>}
      <main id="content" tabIndex={-1} className="x1iyjqo2 xeuugli">
        {children}
      </main>
    </div>;
}